import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client';
import type { Accion, AlcanceClave, CamposTabla, Cargas, Catalogo, ClaveApi, ClaveApiNueva, Diff, Estado, EventoAuditoria, Me, Version, VersionResumen } from '../types/api';

export const useMe = () => useQuery({ queryKey: ['me'], queryFn: () => api.get<Me>('/api/me'), staleTime: 60_000, retry: false });

export const useConfig = () => useQuery({ queryKey: ['config'], queryFn: () => api.get<Version>('/api/config') });

export const useEstado = () =>
  useQuery({ queryKey: ['estado'], queryFn: () => api.get<Estado>('/api/estado'), refetchInterval: 60_000 });

/** Catálogo de cargas al DWH: dwh_salud lo envía cada hora, así que basta con mirarlo cada 5 min. */
export const useCargas = () =>
  useQuery({ queryKey: ['cargas'], queryFn: () => api.get<Cargas>('/api/cargas'), refetchInterval: 300_000 });

export const useVersiones = (tabla?: string) =>
  useQuery({
    queryKey: ['versiones', tabla ?? null],
    queryFn: () =>
      api.get<{ versiones: VersionResumen[] }>(`/api/config/versiones?limite=200${tabla ? `&tabla=${encodeURIComponent(tabla)}` : ''}`).then((r) => r.versiones),
  });

export const useDiff = (id: number) =>
  useQuery({ queryKey: ['diff', id], queryFn: () => api.get<Diff>(`/api/config/versiones/${id}/diff`), enabled: Number.isFinite(id) });

export const useAcciones = (tabla?: string) =>
  useQuery({
    queryKey: ['acciones', tabla ?? null],
    queryFn: () => api.get<{ acciones: Accion[] }>(`/api/acciones${tabla ? `?tabla=${encodeURIComponent(tabla)}` : ''}`).then((r) => r.acciones),
  });

export const useAuditoria = (filtro: { accion?: string; recurso_id?: string }) =>
  useQuery({
    queryKey: ['auditoria', filtro],
    queryFn: () => {
      const q = new URLSearchParams({ limite: '200' });
      if (filtro.accion) q.set('accion', filtro.accion);
      if (filtro.recurso_id) q.set('recurso_id', filtro.recurso_id);
      return api.get<{ eventos: EventoAuditoria[] }>(`/api/auditoria?${q}`).then((r) => r.eventos);
    },
  });

export interface CambioTablaPeticion {
  tabla: CamposTabla;
  motivo_cambio: string;
  version_base?: number;
}

/** Tras cualquier cambio de configuración: la vigente, el historial y la auditoría ya no valen. */
function useInvalidarConfig() {
  const qc = useQueryClient();
  return () =>
    Promise.all(['config', 'versiones', 'auditoria', 'diff'].map((k) => qc.invalidateQueries({ queryKey: [k] })));
}

export function useGuardarTabla(nombreExistente?: string) {
  const invalidar = useInvalidarConfig();
  return useMutation({
    mutationFn: (p: CambioTablaPeticion) =>
      nombreExistente
        ? api.put<Version>(`/api/config/tablas/${encodeURIComponent(nombreExistente)}`, p)
        : api.post<Version>('/api/config/tablas', p),
    onSuccess: invalidar,
  });
}

export function useDesactivarTabla(nombre: string) {
  const invalidar = useInvalidarConfig();
  return useMutation({
    mutationFn: (p: { motivo_cambio: string; version_base?: number }) =>
      api.del<Version>(`/api/config/tablas/${encodeURIComponent(nombre)}`, p),
    onSuccess: invalidar,
  });
}

export function usePedirRecarga(tabla: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (motivo: string) => api.post<Accion>('/api/acciones', { tipo: 'recargar', tabla, motivo }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['acciones'] }),
  });
}

export const useClaves = (todas: boolean) =>
  useQuery({
    queryKey: ['claves', todas],
    queryFn: () => api.get<{ claves: ClaveApi[] }>(`/api/claves${todas ? '?todas=1' : ''}`).then((r) => r.claves),
  });

export function useCrearClave() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (p: { nombre: string; alcance: AlcanceClave; dias: number }) => api.post<ClaveApiNueva>('/api/claves', p),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['claves'] }),
  });
}

export function useRevocarClave() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.del<ClaveApi>(`/api/claves/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['claves'] }),
  });
}

/** Columnas de X3 de una tabla. Mientras Airflow la está leyendo, se vuelve a mirar cada 5 s. */
export const useCatalogo = (tabla: string | null) =>
  useQuery({
    queryKey: ['catalogo', tabla],
    queryFn: () => api.get<Catalogo>(`/api/catalogo/${encodeURIComponent(tabla ?? '')}`),
    enabled: !!tabla,
    refetchInterval: (q) => (q.state.data?.pendiente ? 5_000 : false),
  });

export function usePedirCatalogo(tabla: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<Catalogo>(`/api/catalogo/${encodeURIComponent(tabla)}`),
    onSuccess: (c) => qc.setQueryData(['catalogo', tabla], c),
  });
}
